import type { JSX } from 'react';
import { useIntl } from 'react-intl';

import * as styles from './DataTableDownloadData.sty.js';
import { Menu } from '../../../../../navigation/menu/Menu.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../../messages.js';

/**
 * A component providing the menu with the functionalities to download data.
 * @internal
 */
export const DataTableDownloadData = ({
  excludeColumns,
  hasIntents,
  isDownloading,
  onDownloadStart,
  onDownloadEnd,
}: {
  excludeColumns?: string[];
  hasIntents: boolean;
  isDownloading: boolean;
  onDownloadStart: () => void;
  onDownloadEnd: () => void;
}): JSX.Element => {
  const intl = useIntl();
  const table = useDataTable();
  const selectionCount = table.getSelectionCount();
  const menuGroupLabel = intl.formatMessage(dataTableMessages.downloadTable);

  const downloadMenuItems = (
    <>
      <Menu.Item
        onSelect={() => {
          if (isDownloading) {
            return;
          }
          onDownloadStart();
          table.downloadAllData(excludeColumns, onDownloadEnd);
        }}
        className={styles.downloadMenuItem}
        data-dt-name="ToolbarDownloadAll"
      >
        {intl.formatMessage(dataTableMessages.downloadAll)}
        <Menu.Suffix className={styles.downloadMenuSuffix}>
          {intl.formatMessage(dataTableMessages.downloadRowsCount, {
            noOfRows: table.getPrePaginatedRowModel().flatRows.length,
          })}
        </Menu.Suffix>
      </Menu.Item>
      {table.options.enablePagination && (
        <Menu.Item
          onSelect={() => {
            if (isDownloading) {
              return;
            }
            onDownloadStart();
            table.downloadPageData(excludeColumns, onDownloadEnd);
          }}
          className={styles.downloadMenuItem}
          data-dt-name="ToolbarDownloadPage"
        >
          {intl.formatMessage(dataTableMessages.downloadPage)}
          <Menu.Suffix className={styles.downloadMenuSuffix}>
            {intl.formatMessage(dataTableMessages.downloadRowsCount, {
              noOfRows: table.getPaginatedRowModel().flatRows.length,
            })}
          </Menu.Suffix>
        </Menu.Item>
      )}
      {table.options.enableRowSelection && (
        <Menu.Item
          disabled={selectionCount === 0}
          onSelect={() => {
            if (isDownloading) {
              return;
            }
            onDownloadStart();
            table.downloadSelectedData(excludeColumns, onDownloadEnd);
          }}
          className={styles.downloadMenuItem}
          data-dt-name="ToolbarDownloadSelected"
        >
          {intl.formatMessage(dataTableMessages.downloadSelected)}
          <Menu.Suffix className={styles.downloadMenuSuffix}>
            {intl.formatMessage(dataTableMessages.downloadRowsCount, {
              noOfRows: selectionCount,
            })}
          </Menu.Suffix>
        </Menu.Item>
      )}
    </>
  );

  return hasIntents ? (
    <Menu.Sub defaultOpen={false}>
      <Menu.SubTrigger>{menuGroupLabel}</Menu.SubTrigger>
      <Menu.SubContent>{downloadMenuItems}</Menu.SubContent>
    </Menu.Sub>
  ) : (
    <>
      <Menu.Label>{menuGroupLabel}</Menu.Label>
      {downloadMenuItems}
    </>
  );
};
